// Pure geometry helpers for defense plans. No Leaflet, no Firebase — the
// zustand store and the draw hook (useDefensePlanDraw.web.ts) both import
// this, and the draw hook needs to validate a line before any Firestore
// write is even attempted.
import { DefensePlan, DefensePlanFeature, DefenseStrategy, PlanBounds } from '../types';
import { MAX_FEATURES_PER_PLAN, MAX_VERTICES_PER_LINE } from '../schemas/firestoreSchemas';
import { AppError } from './errorHandler';
import { pointInPolygon } from './geo';
import { pathLengthMeters, ringAreaSquareMeters } from './geoMeasure';

// A slim shape covering just what add/remove need, so callers can pass
// either a full DefensePlan or an in-progress plan literal without pulling
// in the whole type. Not exported: DefensePlan already satisfies it
// structurally, and no other file needs to spell it out.
interface PlanFeatureSet {
  features: DefensePlanFeature[];
  featureCount: number;
  bounds: PlanBounds | null;
}

// Bounding box of one feature's coords. A point feature (a single coord)
// returns a zero-area box at that point — callers that need padding (e.g.
// Map.web.tsx's focusOnBounds) add it themselves.
export function featureBounds(feature: DefensePlanFeature): PlanBounds {
  let n = -Infinity;
  let s = Infinity;
  let e = -Infinity;
  let w = Infinity;
  for (const { lat, lng } of feature.coords) {
    if (lat > n) n = lat;
    if (lat < s) s = lat;
    if (lng > e) e = lng;
    if (lng < w) w = lng;
  }
  return { n, s, e, w };
}

// Bounding box across every feature in the plan. Null for an empty plan —
// there's nothing to fit the map to, and PlanBoundsSchema is nullable for
// exactly this case.
export function planBounds(features: DefensePlanFeature[]): PlanBounds | null {
  if (features.length === 0) return null;
  let n = -Infinity;
  let s = Infinity;
  let e = -Infinity;
  let w = Infinity;
  for (const feature of features) {
    const b = featureBounds(feature);
    if (b.n > n) n = b.n;
    if (b.s < s) s = b.s;
    if (b.e > e) e = b.e;
    if (b.w < w) w = b.w;
  }
  return { n, s, e, w };
}

// How many features of each strategy a plan has, for the legend group and
// MapLayersSheet's "N defenses" line. Strategies with zero features are
// left out of the record rather than present at 0.
export function strategyCounts(
  features: DefensePlanFeature[]
): Partial<Record<DefenseStrategy, number>> {
  const counts: Partial<Record<DefenseStrategy, number>> = {};
  for (const feature of features) {
    counts[feature.strategy] = (counts[feature.strategy] ?? 0) + 1;
  }
  return counts;
}

// Adds a feature and recomputes featureCount/bounds, returning a new plan
// object (the store applies this optimistically, so it must not mutate the
// input). Refuses past MAX_FEATURES_PER_PLAN by throwing rather than
// silently returning the plan unchanged: a silent no-op would leave the
// draw hook's freshly-built feature looking committed when it never was,
// and the store already wraps every write in try/catch to revert
// optimistic state on failure — a thrown AppError reuses that same path
// instead of needing a second "did it actually apply" check.
export function applyAddFeature<T extends PlanFeatureSet>(plan: T, feature: DefensePlanFeature): T {
  if (plan.features.length >= MAX_FEATURES_PER_PLAN) {
    throw new AppError(
      `A plan can hold at most ${MAX_FEATURES_PER_PLAN} features.`,
      'VALIDATION_ERROR'
    );
  }
  const features = [...plan.features, feature];
  return {
    ...plan,
    features,
    featureCount: features.length,
    bounds: planBounds(features),
  };
}

// Removes a feature by id and recomputes featureCount/bounds. No-op
// (returns the same plan reference) when the id isn't present, so a
// double-delete (e.g. a slow tap plus a retry) doesn't throw.
export function applyRemoveFeature<T extends PlanFeatureSet>(plan: T, featureId: string): T {
  if (!plan.features.some((f) => f.id === featureId)) return plan;
  const features = plan.features.filter((f) => f.id !== featureId);
  return {
    ...plan,
    features,
    featureCount: features.length,
    bounds: planBounds(features),
  };
}

// A line needs at least 2 vertices to draw and at most MAX_VERTICES_PER_LINE
// (the schema's cap, backstopped by the 1 MiB doc limit). Point features
// don't go through this check — the draw hook commits a point on a single
// click.
export function isCommittableLine(coords: { lat: number; lng: number }[]): boolean {
  return coords.length >= 2 && coords.length <= MAX_VERTICES_PER_LINE;
}

// Same idea as isCommittableLine, for a closed ring: an area needs at least
// 3 vertices to enclose any space, same upper cap as a line.
export function isCommittableArea(coords: { lat: number; lng: number }[]): boolean {
  return coords.length >= 3 && coords.length <= MAX_VERTICES_PER_LINE;
}

// Collapses trailing vertices that sit within epsilonDeg of the vertex
// before them. A browser dblclick fires two click events before the
// dblclick event itself, so the line-drawing hook's click handler has
// already appended one or two near-identical vertices at the end of the
// list by the time dblclick runs to commit it; call this first so the
// commit doesn't ship a zero-length final segment. Pops repeatedly (not
// just once) since a dblclick can leave two stacked duplicates, not one.
// Default epsilon (~1cm at the equator) is far below any real short
// segment a user draws on purpose.
export function dropTrailingDuplicates(
  coords: { lat: number; lng: number }[],
  epsilonDeg = 1e-7
): { lat: number; lng: number }[] {
  const out = [...coords];
  while (out.length >= 2) {
    const { lat: lat1, lng: lng1 } = out[out.length - 1];
    const { lat: lat2, lng: lng2 } = out[out.length - 2];
    if (Math.abs(lat1 - lat2) <= epsilonDeg && Math.abs(lng1 - lng2) <= epsilonDeg) {
      out.pop();
    } else {
      break;
    }
  }
  return out;
}

// Distance and area math lives in geoMeasure.ts (one haversine, one ring-area
// formula). Re-exported here because planHomes.ts's nearestStructure and the
// panel already import it from this file.
export { haversineMeters } from './geoMeasure';

// Total length of a line (or any open path) in meters. The panel's feature
// list shows this instead of a vertex count: a line's vertices are bends, not
// the thing being measured.
export function featureLength(coords: { lat: number; lng: number }[]): number {
  return pathLengthMeters(coords);
}

// Area enclosed by a ring, in square meters (spherical formula, see
// geoMeasure.ringAreaSquareMeters). Coords need not be closed.
export function featureArea(coords: { lat: number; lng: number }[]): number {
  return ringAreaSquareMeters(coords);
}

// Every plan whose AREA feature of a given strategy encloses the point,
// grouped by strategy — the property page's "neighborhood plans suggest"
// line reads this directly (src/components/structure-detail/PlanSuggestions).
// Line and point features never match: only an area asserts "this ground",
// which is the only kind a point can be inside or outside of. Callers are
// responsible for pre-filtering `plans` to whatever's visible to the current
// viewer (see the areaId + visibility='shared' scoping subscribeAreaPlans
// uses for the map's own "plans in this area" list) — this function doesn't
// re-derive that rule, it only tests containment.
export function suggestionsForPoint(
  plans: DefensePlan[],
  lat: number,
  lng: number
): Partial<Record<DefenseStrategy, DefensePlan[]>> {
  const result: Partial<Record<DefenseStrategy, DefensePlan[]>> = {};
  for (const plan of plans) {
    // A plan whose last feature was just deleted keeps its doc, empty,
    // rather than being removed (undo needs the id — see
    // defensePlansStore.removeFeature); its inner loop would find nothing
    // anyway, but skip it up front rather than iterate zero features.
    if (plan.features.length === 0) continue;
    for (const feature of plan.features) {
      if (feature.kind !== 'area' || feature.coords.length < 3) continue;
      const ring: [number, number][] = feature.coords.map(({ lat: y, lng: x }) => [x, y]);
      const [firstX, firstY] = ring[0];
      const [lastX, lastY] = ring[ring.length - 1];
      if (firstX !== lastX || firstY !== lastY) ring.push([firstX, firstY]);
      const inside = pointInPolygon(lat, lng, {
        type: 'Feature',
        properties: {},
        geometry: { type: 'Polygon', coordinates: [ring] },
      });
      if (!inside) continue;
      const existing = result[feature.strategy] ?? [];
      if (!existing.some((p) => p.id === plan.id)) {
        result[feature.strategy] = [...existing, plan];
      }
    }
  }
  return result;
}

// Every distinct strategy a shared plan has applied directly to this
// structure (a "home" feature — kind 'point' with `structureId` set, see
// DefensePlanFeatureSchema's comment), deduped by strategy — the property
// page's "Planned for this home" line (PlanSuggestions.tsx) reads this
// directly. Mirrors suggestionsForPoint's shape and pre-filtering contract
// (callers scope `plans` to areaId + visibility:'shared' first) but keys off
// an exact structureId match rather than polygon containment, so it also
// finds a home feature attached to a structure outside the plan's own area
// (a plan's areaId comes from its first vertex, so a later home feature
// added elsewhere doesn't change it). Keeps the whole plan, not just the
// strategy, so the caller can link to it and read its title.
export function suggestionsForStructure(
  plans: DefensePlan[],
  structureId: string
): { strategy: DefenseStrategy; plan: DefensePlan }[] {
  const seen = new Set<DefenseStrategy>();
  const result: { strategy: DefenseStrategy; plan: DefensePlan }[] = [];
  for (const plan of plans) {
    if (plan.features.length === 0) continue;
    for (const feature of plan.features) {
      if (feature.structureId !== structureId) continue;
      if (seen.has(feature.strategy)) continue;
      seen.add(feature.strategy);
      result.push({ strategy: feature.strategy, plan });
    }
  }
  return result;
}

// Most recently updated plan, or null. The own-plan subscription filters by
// owner only and picks the newest here (see defensePlansApi.subscribeOwnPlan).
export function newestPlan(plans: DefensePlan[]): DefensePlan | null {
  let newest: DefensePlan | null = null;
  for (const plan of plans) {
    if (!newest || plan.updatedAt.toMillis() > newest.updatedAt.toMillis()) newest = plan;
  }
  return newest;
}

/** The vertex list without its last point; the input is not changed. */
export function removeLastVertex<T>(vertices: T[]): T[] {
  return vertices.slice(0, -1);
}

export type StrokeOutcome =
  | { status: 'commit'; coords: { lat: number; lng: number }[] }
  // Too few distinct points to make the shape yet. The caller keeps the
  // stroke open with the trailing duplicates already dropped.
  | { status: 'too-short'; vertices: { lat: number; lng: number }[] }
  // Past the vertex cap: nothing the user can add will fix it.
  | { status: 'too-long' };

/**
 * What finishing (double-click, Enter, click on the first corner) does with
 * the vertices placed so far. Drops the duplicates a double-click leaves,
 * then checks the shape.
 */
export function finishStroke(
  kind: 'line' | 'area',
  vertices: { lat: number; lng: number }[]
): StrokeOutcome {
  const cleaned = dropTrailingDuplicates(vertices);
  if (cleaned.length > MAX_VERTICES_PER_LINE) return { status: 'too-long' };
  const committable = kind === 'area' ? isCommittableArea(cleaned) : isCommittableLine(cleaned);
  return committable
    ? { status: 'commit', coords: cleaned }
    : { status: 'too-short', vertices: cleaned };
}
